import { api } from './api';
import {
  Invoice,
  CreateInvoiceData,
  UpdateInvoiceData,
  InvoiceStats,
  InvoicesResponse,
  InvoiceFilters
} from '../types/invoice';

export const invoiceService = {
  async getInvoices(filters: InvoiceFilters = {}): Promise<InvoicesResponse> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.customerId) params.append('customerId', filters.customerId);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    const response = await api.get(`/invoices?${params.toString()}`);
    return response.data;
  },

  async getInvoice(id: string): Promise<Invoice> {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  },

  async createInvoice(data: CreateInvoiceData): Promise<Invoice> {
    const response = await api.post('/invoices', data);
    return response.data;
  },

  async updateInvoice(id: string, data: UpdateInvoiceData): Promise<Invoice> {
    const response = await api.put(`/invoices/${id}`, data);
    return response.data;
  },

  async deleteInvoice(id: string): Promise<void> {
    const response = await api.delete(`/invoices/${id}`);
    return response.data;
  },

  async getInvoiceStats(): Promise<InvoiceStats> {
    const response = await api.get('/invoices/stats');
    return response.data;
  },

  async markInvoiceAsSent(id: string): Promise<Invoice> {
    console.log('📤 invoiceService.markInvoiceAsSent called with id:', id);
    console.log('📤 Making PATCH request to:', `/invoices/${id}/send`);
    console.log('📤 API baseURL:', api.defaults.baseURL);
    const response = await api.patch(`/invoices/${id}/send`);
    console.log('✅ Response received:', response);
    return response.data;
  },

  async cancelInvoice(id: string): Promise<Invoice> {
    const response = await api.patch(`/invoices/${id}/cancel`);
    return response.data;
  },

  // Helper function to calculate line totals
  calculateLineTotal(quantity: number, rate: number, taxRate: number = 0): { amount: number; taxAmount: number; total: number } {
    const amount = quantity * rate;
    const taxAmount = (amount * taxRate) / 100;
    const total = amount + taxAmount;
    
    return { amount, taxAmount, total };
  },

  // Helper function to calculate invoice totals
  calculateInvoiceTotals(items: { quantity: number; rate: number; taxRate?: number }[]): { subtotal: number; taxAmount: number; total: number } {
    let subtotal = 0;
    let taxAmount = 0;
    
    items.forEach(item => {
      const lineTotal = this.calculateLineTotal(item.quantity, item.rate, item.taxRate || 0);
      subtotal += lineTotal.amount;
      taxAmount += lineTotal.taxAmount;
    });
    
    return { subtotal, taxAmount, total: subtotal + taxAmount };
  },

  // Helper function to format invoice number
  formatInvoiceNumber(number: string): string {
    return number.startsWith('INV-') ? number : `INV-${number}`;
  },

  // Helper function to check if invoice can be edited
  canEditInvoice(status: string): boolean {
    return !['PAID', 'CANCELLED'].includes(status);
  },

  // Helper function to check if invoice can be deleted
  canDeleteInvoice(status: string, hasPayments: boolean): boolean {
    return status === 'DRAFT' && !hasPayments;
  },

  // Helper function to get next status options
  getAvailableStatusTransitions(currentStatus: string): string[] {
    switch (currentStatus) {
      case 'DRAFT':
        return ['SENT', 'CANCELLED'];
      case 'SENT':
        return ['VIEWED', 'PAID', 'PARTIALLY_PAID', 'OVERDUE', 'CANCELLED'];
      case 'VIEWED':
        return ['PAID', 'PARTIALLY_PAID', 'OVERDUE', 'CANCELLED'];
      case 'OVERDUE':
        return ['PAID', 'PARTIALLY_PAID', 'CANCELLED'];
      case 'PARTIALLY_PAID':
        return ['PAID', 'OVERDUE', 'CANCELLED'];
      default:
        return [];
    }
  }
};